import { useState } from 'react';
import { infoToolTip } from '../../assets/hostedassets';
import LoadingSpinner from '../../common/_custom/LoadingSpinner/LoadingSpinner';
import styles from './RiskIndicators.module.scss';
import RiskIndicatorTable from './RiskIndicatorTable/RiskIndicatorTable';
import Tooltip from '../../common/_custom/Tooltip/Tooltip';
import { RISK_HEADER_KEYS, RISK_MESSAGES_MAP } from './RiskIndicatorConstants';
import clsx from 'clsx';
import { useRiskIndicatorStates } from './useRiskIndicatorStates';
import A4Page from '../AicaPDFReport/A4Page/A4Page';
import { REPORT_SECTION_NAMES } from '../../store/pdfReport/pdfReport_constants';
import RiskIndicatorCard from './RiskIndicatorCard';
import HeaderBodyBox from '../../common/HeaderBodyBox/HeaderBodyBox';
import AskAicaQuestionBadge from '../../common/AskAicaQuestionBadge/AskAicaQuestionBadge';
import FileViewerDrawer from '../../common/DocumentViewer/FileViewerDrawer';

interface RiskProfile {
    [key: string]: number;
}

export interface Row {
    docId?: string;
    name: string;
    type: string;
    riskProfile: RiskProfile;
}

interface ViewerState {
    url: string;
    mimeType: string;
    heading: string;
    docId: string;
}

const RiskIndicators = ({ isPDFReport = false }) => {
    const [rows, setRows] = useState<Row[]>([]);
    const [viewerState, setViewerState] = useState<ViewerState | null>(null);

    const { loading, getStatusMessage, RiskDotMessage, handleViewReport, downloadReport } = useRiskIndicatorStates({
        rows,
        setRows,
        autoFetch: true,
    });

    const onViewReport = async (docId: string, name: string) => {
        const result = await handleViewReport(docId, name);
        if (result) setViewerState(result);
    };

    const closeViewer = () => setViewerState(null);

    const headerRight = (
        <div className={styles.DataMessage}>
            <span className={styles.Loader}>{loading && <LoadingSpinner color="#6021B3" height="1.125rem" />}</span>
            <span className={styles.Message}>{getStatusMessage()}</span>
        </div>
    );

    const titleWithTooltip = (
        <div className={styles.HeadingMessage}>
            <span>Regulatory Checks</span>
            <span className={styles.InfoIcon}>
                {!isPDFReport && (
                    <Tooltip
                        content={RISK_MESSAGES_MAP[RISK_HEADER_KEYS.RISK_INDICATOR_KEY] || ''}
                        direction="right"
                        style={{ top: '100%' }}
                    >
                        <img src={infoToolTip} height={'14px'} />
                    </Tooltip>
                )}
            </span>
        </div>
    );

    const bodyContent = rows?.length > 0 && !loading && (
        <>
            {isPDFReport ? (
                <div className={styles.CardsContainer}>
                    {rows.map((row, index) => (
                        <RiskIndicatorCard key={index} row={row} />
                    ))}
                </div>
            ) : (
                <div className={styles.TableWrapper}>
                    <div className={styles.TableContainer}>
                        <RiskIndicatorTable rows={rows} setRows={setRows} isPDFReport={isPDFReport} onViewReport={onViewReport} />
                    </div>
                </div>
            )}
            <div className={styles.FooterContainer}>
                <div className={styles.FooterMessage}>
                    <span className={styles.Note}>Note:</span> Risks are flagged based on the legal name match (please verify).
                </div>
                {/* <div className={styles.RiskDotMessage}>{RiskDotMessage()}</div> */}
            </div>
        </>
    );

    const render = (
        <div className={clsx(styles.ContainerWrapper, { [styles.PDFReport]: isPDFReport })}>
            <HeaderBodyBox
                title={!isPDFReport ? titleWithTooltip : 'Regulatory Checks'}
                headerRight={headerRight}
                className={clsx({ [styles.DataAvailable]: rows?.length > 0 && !loading })}
                isPDFReport={isPDFReport}
            >
                {bodyContent}
            </HeaderBodyBox>
        </div>
    );

    if (isPDFReport) return rows?.length ? <A4Page sectionName={REPORT_SECTION_NAMES.REGULATORY_INDICATORS}>{render}</A4Page> : <></>;

    return (
        <>
            {render}
            {!!viewerState && (
                <FileViewerDrawer
                    heading={viewerState.heading}
                    fileUrl={viewerState.url}
                    mimeType={viewerState.mimeType}
                    onDownload={() => {
                        downloadReport(viewerState.docId, viewerState.heading);
                    }}
                    onClose={closeViewer}
                />
            )}
        </>
    );
};

export default RiskIndicators;
